import { PlayArrow, ReplayCircleFilledOutlined } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Room, RoomEvent } from "livekit-client";
import { LinearProgress } from "@gpstrackit/gtc-react-controls";
import { useIntl } from "react-intl";
import {
  resetMitac,
  startMitacLiveStreaming,
  joinMitacLiveStreaming,
  stopMitacLiveStreaming,
} from "./redux/slice";

const STREAM_MAX_DURATION_SECONDS = 300;
const STREAM_WARNING_THRESHOLD_SECONDS = 30;
const JOIN_POLL_INTERVAL_MS = 3000;
const JOIN_MAX_ATTEMPTS = 10; // ~30s of polling while the device wakes up before giving up

const formatTime = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export const VideoPlayer = ({ children, retryCallback }) => {
  const dispatch = useDispatch();

  const handleRetry = useCallback(() => {
    dispatch(resetMitac());
    retryCallback?.();
  }, [dispatch, retryCallback]);

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="100%"
      height="100%"
      bgcolor="black"
      flexDirection="column"
      gap={2}
      style={{ padding: "24px 24px 10px 24px", boxSizing: "border-box" }}
    >
      {children}

      {retryCallback && (
        <IconButton onClick={(e) => { e.stopPropagation(); handleRetry(); }} size="large" style={{ color: "white" }}>
          <ReplayCircleFilledOutlined style={{ color: "white", fontSize: 35 }} />
        </IconButton>
      )}
    </Box>
  );
};

export const Playback = ({ handlePlay, isLoading }) => {
  return (
    <VideoPlayer>
      {isLoading ? (
        <Box width="200px">
          <LinearProgress />
        </Box>
      ) : (
        <PlayArrow
          onClick={(e) => {
            e.stopPropagation();
            handlePlay();
          }}
          style={{ color: "white", fontSize: 50, cursor: "pointer" }}
        />
      )}
    </VideoPlayer>
  );
};

export const MitacVideo = ({ deviceId }) => {
  const dispatch = useDispatch();
  const { messages } = useIntl();
  const videoRef = useRef(null);
  const roomRef = useRef(null);
  const timerRef = useRef(null);
  const activeTrackRef = useRef(null);

  const { livekitUrl, room, token, error, loading } =
    useSelector((state) => state.mitacRedux);

  const [phase, setPhase] = useState("idle"); // idle | connecting | live | offline | error | ended
  const [remainingSeconds, setRemainingSeconds] = useState(STREAM_MAX_DURATION_SECONDS);
  const stageRef = useRef("start"); // "start" | "join" — start only wakes the device, join returns the token
  const joinAttemptsRef = useRef(0);
  const pollTimeoutRef = useRef(null);

  const disconnectRoom = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    clearTimeout(pollTimeoutRef.current);
    pollTimeoutRef.current = null;
    if (roomRef.current) {
      roomRef.current.disconnect();
      roomRef.current = null;
    }
    activeTrackRef.current = null;
  }, []);

  const handleStop = useCallback(() => {
    disconnectRoom();
    if (deviceId) dispatch(stopMitacLiveStreaming({ deviceId }));
    dispatch(resetMitac());
    setPhase("ended");
  }, [deviceId, dispatch, disconnectRoom]);

  const handlePlay = useCallback(() => {
    stageRef.current = "start";
    joinAttemptsRef.current = 0;
    setPhase("connecting");
    dispatch(startMitacLiveStreaming({ deviceId }));
  }, [deviceId, dispatch]);

  // Mitac's protocol is two separate calls: startMitacLiveStreaming just wakes the device
  // (its token is always null), and joinMitacLiveStreaming is the one that actually returns
  // the LiveKit token once the device has connected — which can take a few seconds, so join
  // is polled with a delay instead of called once.
  useEffect(() => {
    if (loading || phase !== "connecting") return;

    if (error) {
      setPhase("error");
      return;
    }

    if (stageRef.current === "start") {
      stageRef.current = "join";
      pollTimeoutRef.current = setTimeout(() => {
        dispatch(joinMitacLiveStreaming({ deviceId }));
      }, JOIN_POLL_INTERVAL_MS);
      return;
    }

    if (!livekitUrl || !room || !token) {
      if (joinAttemptsRef.current < JOIN_MAX_ATTEMPTS) {
        joinAttemptsRef.current += 1;
        pollTimeoutRef.current = setTimeout(() => {
          dispatch(joinMitacLiveStreaming({ deviceId }));
        }, JOIN_POLL_INTERVAL_MS);
      } else {
        setPhase("offline");
      }
      return;
    }

    let cancelled = false;
    const liveKitRoom = new Room();
    roomRef.current = liveKitRoom;

    liveKitRoom.on(RoomEvent.TrackSubscribed, (track) => {
      // A track can arrive before the <video> element mounts (it only renders once
      // phase becomes "live"); keep it and let the phase==="live" effect below attach it.
      activeTrackRef.current = track;
      if (videoRef.current) track.attach(videoRef.current);
    });

    liveKitRoom.on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach();
      if (activeTrackRef.current === track) activeTrackRef.current = null;
    });

    liveKitRoom.on(RoomEvent.Disconnected, () => {
      if (!cancelled) handleStop();
    });

    liveKitRoom
      .connect(livekitUrl, token)
      .then(() => {
        if (cancelled) return;
        setRemainingSeconds(STREAM_MAX_DURATION_SECONDS);
        setPhase("live");
      })
      .catch(() => {
        if (!cancelled) setPhase("error");
      });

    return () => {
      cancelled = true;
    };
  }, [loading, error, livekitUrl, room, token, phase, deviceId, dispatch, handleStop]);

  // The <video> element only mounts once phase is "live" — attach whatever track
  // already arrived (possibly before this element existed) as soon as it's available.
  useEffect(() => {
    if (phase === "live" && videoRef.current && activeTrackRef.current) {
      activeTrackRef.current.attach(videoRef.current);
    }
  }, [phase]);

  // Live sessions are capped server-side at 5 minutes; mirror that limit client-side
  useEffect(() => {
    if (phase !== "live") return;

    timerRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          handleStop();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [phase, handleStop]);

  useEffect(() => {
    return () => {
      disconnectRoom();
      if (deviceId) dispatch(stopMitacLiveStreaming({ deviceId }));
      dispatch(resetMitac());
    };
  }, []);

  if (phase === "offline") {
    return (
      <VideoPlayer retryCallback={handlePlay}>
        <span style={{ color: "white", fontSize: 16, textAlign: "center", display: "inline-block", maxWidth: "70%" }}>
          {messages["mitacVideo_deviceOffline"]}
        </span>
      </VideoPlayer>
    );
  }

  if (phase === "error") {
    return (
      <VideoPlayer retryCallback={handlePlay}>
        <span style={{ color: "white", fontSize: 16, textAlign: "center", display: "inline-block", maxWidth: "70%" }}>
          {messages["mitacVideo_error"]}
        </span>
      </VideoPlayer>
    );
  }

  if (phase === "live") {
    const isEndingSoon = remainingSeconds <= STREAM_WARNING_THRESHOLD_SECONDS;
    return (
      <Box position="relative" width="100%" height="100%" bgcolor="black">
        <video
          ref={videoRef}
          autoPlay
          style={{ width: "100%", height: "100%", backgroundColor: "black" }}
        />
        <Typography
          variant="caption"
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            color: isEndingSoon ? "#ff5252" : "white",
            backgroundColor: "rgba(0,0,0,0.5)",
            padding: "2px 8px",
            borderRadius: 4,
          }}
        >
          {formatTime(remainingSeconds)}
        </Typography>
      </Box>
    );
  }

  return <Playback handlePlay={handlePlay} isLoading={phase === "connecting" || loading} />;
};

export default MitacVideo;
